import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "matchbroker.access_token";

/**
 * SECURITY NOTE — JWT storage tradeoff (deliberate):
 * MatchBroker uses stateless Bearer-token auth so the same API can be
 * consumed by native iOS/Android clients that cannot participate in a
 * browser cookie jar. That REQUIRES storing the token in JS-reachable
 * storage on web. We chose localStorage over httpOnly cookies because:
 *   1. Native mobile clients need to read the token to inject
 *      `Authorization: Bearer <token>` on every request.
 *   2. A cookie-based approach would either force a hybrid auth path
 *      (web = cookie, mobile = bearer) or require reflecting the cookie
 *      back to JS on mobile — negating the security benefit.
 *
 * The XSS risk this introduces is mitigated by:
 *   - No `dangerouslySetInnerHTML` anywhere in `src/` (grep-enforced).
 *   - React auto-escapes every rendered string.
 *   - The token is only sent as the Authorization header — never in
 *     URLs, query strings, or the request body (see request interceptor
 *     below).
 *   - Minimal third-party script surface (fonts + Sonner + shadcn UI).
 *   - Short-lived access tokens (7d) issued by the backend.
 * See /app/memory/PRD.md "Security decisions" for the full rationale.
 */
export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setStoredToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  // Attach Bearer header — never place the token in URLs or query strings.
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Safely extract a human-readable error message from an axios error.
 * FastAPI 422 returns an array of {msg,loc,...} objects — rendering that
 * in JSX crashes React, so we normalize to a string here AND map the
 * field name in `loc` to a friendly label so the user sees "Building /
 * project is required" instead of "String should have at least 1
 * character".
 */
const FIELD_LABELS = {
  raw_text: "Raw text",
  title: "Title",
  transaction_type: "Transaction type",
  property_category: "Property category",
  location: "Location",
  building_or_project: "Building / project",
  unit_identifier: "Unit",
  bedrooms: "Bedrooms",
  lot_area_sqm: "Lot area",
  floor_area_sqm: "Floor area",
  parking_slots: "Parking slots",
  price_gross: "Price",
  price: "Price",
  semantic_features: "Key highlights",
  exclusive_ats: "Exclusive ATS",
  target_location: "Target location",
  budget_max: "Max budget",
  preferred_features: "Preferred features",
  notes: "Notes",
  email: "Email",
  password: "Password",
  full_name: "Full name",
  prc_license_number: "PRC license",
};

function _humanize422Item(item) {
  if (!item || typeof item !== "object") return String(item);
  // loc looks like ["body", "field_name"] or ["body", "field_name", 0].
  const loc = Array.isArray(item.loc) ? item.loc.filter((s) => s !== "body") : [];
  const field = loc[0];
  const label = FIELD_LABELS[field] || (field ? String(field).replace(/_/g, " ") : "");
  const msg = String(item.msg || "").trim();
  // Common patterns → user-facing text.
  if (/should have at least 1 character/i.test(msg) || /field required/i.test(msg))
    return label ? `${label} is required.` : "Missing required field.";
  if (/valid number/i.test(msg))
    return label ? `${label} must be a number.` : msg;
  if (/should have at most/i.test(msg))
    return label ? `${label} is too long.` : msg;
  if (/greater than or equal to/i.test(msg))
    return label ? `${label} is out of range.` : msg;
  if (/Input should be/i.test(msg) && field === "property_category")
    return "That property category isn't recognized — please pick one from the list.";
  if (/Input should be/i.test(msg) && field === "transaction_type")
    return "That transaction type isn't recognized — please pick one from the list.";
  return label ? `${label}: ${msg}` : msg;
}

export function formatApiError(err, fallback = "Something went wrong.") {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map(_humanize422Item).filter(Boolean);
    return parts.length ? parts.join(" ") : fallback;
  }
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Return a `{ [field]: "message" }` map from a 422 error, so a form
 * can render field-level inline errors AND a summary toast.
 * Returns null if the error isn't a 422 validation array.
 */
export function fieldErrorsFromApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!Array.isArray(detail)) return null;
  const out = {};
  for (const item of detail) {
    if (!item || typeof item !== "object") continue;
    const loc = Array.isArray(item.loc) ? item.loc.filter((s) => s !== "body") : [];
    const field = loc[0];
    if (!field) continue;
    if (!out[field]) out[field] = _humanize422Item(item);
  }
  return Object.keys(out).length ? out : null;
}
